// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode, Ref } from 'react';

import type { PartListPart } from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';

import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { useUniformFlex } from 'hooks/useUniformFlex';
import { PartCard } from 'modules/Shop/CatalogSetup/PartCard';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

const minCardWidth = 350;
const maxCardWidth = 550;

type Props = {
  parts: PartListPart[];
  selectedCategory: CategoryPath[number] | undefined;
  selectedPart: PartListPart | null;
  /** When provided, part cards are clickable and call this on click. */
  onPartClick?: (part: PartListPart) => void;
  disablePart?: (part: PartListPart) => boolean;
  /** Component to render on the right side of each PartCard. Not rendered on disabled parts. */
  children?: (part: PartListPart) => ReactNode;
  fetchNextPage: () => void;
  loading: boolean;
  /** Attached to the selected part's card so the caller can scroll it into view. */
  scrollToPartRef?: Ref<HTMLDivElement>;
  /** Renders each card at full container width (single-column mobile layout). */
  fullWidthCards?: boolean;
  /** Lays the cards out in a wrapping uniform grid instead of a single column. */
  wrapCards?: boolean;
  /** Pauses uniform-grid layout recalculation, e.g. while a surrounding pane is being resized. */
  suspendLayout?: boolean;
};

/** The scrollable, paginated part-card list shared by `CatalogIntrospector` and `MobileCatalogIntrospector`. */
export const CatalogPartList = ({
  parts,
  selectedCategory,
  selectedPart,
  onPartClick,
  disablePart,
  children,
  fetchNextPage,
  loading,
  scrollToPartRef,
  fullWidthCards,
  wrapCards,
  suspendLayout,
}: Props) => {
  const { containerProps, itemProps } = useUniformFlex(minCardWidth, maxCardWidth, !suspendLayout && !fullWidthCards);

  return (
    <Stack flex={1} style={{ overflowY: 'scroll' }}>
      <Flex
        {...(fullWidthCards
          ? { direction: 'column', gap: 'sm' }
          : { pr: 'xs', ...containerProps, direction: wrapCards ? 'row' : 'column' })}
      >
        {parts.map((part) => {
          const partDisabled = disablePart?.(part);
          return (
            <PartCard
              ref={part.partId === selectedPart?.partId ? scrollToPartRef : undefined}
              key={part.partId}
              part={part}
              fullWidth={fullWidthCards}
              onClick={onPartClick ? () => onPartClick(part) : undefined}
              selected={selectedPart?.partId === part.partId}
              disabled={partDisabled}
              cardProps={fullWidthCards ? undefined : parts.length > 1 && wrapCards ? itemProps : { flex: '1 0 auto' }}
            >
              {!partDisabled && children?.(part)}
            </PartCard>
          );
        })}
      </Flex>
      {isNotNil(selectedCategory) ? (
        <>
          <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
          {parts.length === 0 && !loading && (
            <Flex flex={1} align="center" justify="center">
              <Text c="dimmed" fz="sm">
                No parts found.
              </Text>
            </Flex>
          )}
        </>
      ) : (
        <Flex flex={1} align="center" justify="center">
          <Text c="dimmed" fz="sm">
            Select a category.
          </Text>
        </Flex>
      )}
    </Stack>
  );
};
